import React from 'react';
import SeoHead, {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_META_TITLE,
} from '../components/common/SeoHead';

export default function PrivacyPolicyPage() {
  return (
    <>
      <SeoHead
        title={DEFAULT_META_TITLE}
        description={DEFAULT_META_DESCRIPTION}
        path='/privacy-policy'
        disableTitleSuffix
      />
      <main className='max-w-4xl mx-auto p-6 bg-white'>
        <div className='prose prose-lg max-w-none'>
          <div className='text-center mb-8'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              Privacy Policy
            </h1>
            <p className='text-gray-600'>Effective Date: July 19, 2025</p>
          </div>

          <div className='space-y-8'>
            {/* Introduction */}
            <section>
              <p className='text-gray-700 mb-4'>
                Nexgo AI Labs Inc. (&quot;Nexgo,&quot; &quot;we,&quot;
                &quot;our,&quot; or &quot;us&quot;) is committed to protecting
                your privacy. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your personal information when you visit
                our website www.nexgo.ai, mobile apps, and any related services
                (collectively, the &quot;Service&quot;).
              </p>
              <p className='text-gray-700'>
                By using the Service, you agree to the terms of this Privacy
                Policy. If you do not agree, please do not use the Service.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Information We Collect */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                1. Information We Collect
              </h2>
              <p className='text-gray-700 mb-4'>
                We may collect the following types of information:
              </p>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                a. Information You Provide
              </h3>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>Name, email address, and contact information</li>
                <li>Profile details, preferences, and travel interests</li>
                <li>
                  Payment and billing information (collected via secure payment
                  processors)
                </li>
                <li>
                  Content you upload (itineraries, reviews, messages, photos)
                </li>
              </ul>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                b. Information We Collect Automatically
              </h3>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>IP address, device type, browser type, operating system</li>
                <li>Usage data (pages viewed, links clicked, time on page)</li>
                <li>
                  Location data (via GPS or browser location, with permission)
                </li>
                <li>Cookies and similar technologies (see Section 7)</li>
              </ul>

              <h3 className='text-xl font-semibold text-gray-800 mb-3'>
                c. Information from Third Parties
              </h3>
              <ul className='list-disc pl-6 text-gray-700 space-y-2'>
                <li>Booking partners (e.g., flight, hotel providers)</li>
                <li>Local guides or experience hosts</li>
                <li>Payment gateways (for transaction verification)</li>
                <li>Social platforms (if you connect your account)</li>
              </ul>
            </section>

            <hr className='border-gray-300' />

            {/* How We Use Your Information */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                2. How We Use Your Information
              </h2>
              <p className='text-gray-700 mb-4'>
                We use the collected data to:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2'>
                <li>Provide and operate the Service</li>
                <li>
                  Personalize user experiences using AI-generated
                  recommendations
                </li>
                <li>Facilitate bookings and process payments</li>
                <li>Match users with local guides or experiences</li>
                <li>Improve platform features and content</li>
                <li>Detect and prevent fraud or unauthorized access</li>
                <li>
                  Communicate service updates, offers, and support messages
                </li>
              </ul>
            </section>

            <hr className='border-gray-300' />

            {/* Sharing Your Information */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                3. Sharing Your Information
              </h2>
              <p className='text-gray-700 mb-4'>
                We may share your information with:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>
                  Third-Party Travel Providers: to facilitate your bookings or
                  requests
                </li>
                <li>Payment Processors: to complete financial transactions</li>
                <li>
                  Local Buddies or Experience Hosts: to connect you with
                  services you&apos;ve booked
                </li>
                <li>
                  Vendors and Service Providers: such as analytics and cloud
                  hosting partners
                </li>
                <li>
                  Legal Authorities: when required by law or to protect our
                  legal rights
                </li>
              </ul>
              <p className='text-gray-700'>
                We do not sell your personal information.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Children's Privacy */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                4. Children&apos;s Privacy
              </h2>
              <p className='text-gray-700'>
                Nexgo is intended for users aged 13 and older. We do not
                knowingly collect personal data from children under 13. If we
                learn that a child has provided personal data, we will delete it
                promptly.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Data Retention */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                5. Data Retention
              </h2>
              <p className='text-gray-700'>
                We retain your personal data for as long as necessary to provide
                the Service and fulfill the purposes described in this Privacy
                Policy, including legal, regulatory, or compliance obligations.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Your Choices */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                6. Your Choices
              </h2>
              <ul className='list-disc pl-6 text-gray-700 space-y-2'>
                <li>
                  Profile Settings: You can review and update your information
                  via your account.
                </li>
                <li>
                  Marketing Communications: You may opt-out by clicking
                  &quot;unsubscribe&quot; in our emails.
                </li>
                <li>
                  Location Sharing: You can disable location permissions in your
                  device settings.
                </li>
                <li>
                  Account Deletion: You may request account deletion by
                  contacting support at legal@nexgo.ai.
                </li>
              </ul>
            </section>

            <hr className='border-gray-300' />

            {/* Cookies and Tracking Technologies */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                7. Cookies and Tracking Technologies
              </h2>
              <p className='text-gray-700 mb-4'>
                We use cookies and similar technologies to:
              </p>
              <ul className='list-disc pl-6 text-gray-700 space-y-2 mb-4'>
                <li>Remember user preferences</li>
                <li>Analyze usage trends</li>
                <li>Deliver personalized content</li>
              </ul>
              <p className='text-gray-700'>
                You can control cookies through your browser settings. Disabling
                cookies may limit certain features.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Data Security */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                8. Data Security
              </h2>
              <p className='text-gray-700'>
                We implement administrative, technical, and physical safeguards
                to protect your data. While we strive to protect your personal
                information, no method of transmission or storage is completely
                secure.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* International Data Transfers */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                9. International Data Transfers
              </h2>
              <p className='text-gray-700'>
                If you are accessing the Service outside the United States, you
                consent to the transfer and processing of your information in
                the United States and other jurisdictions with different data
                protection laws.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Third-Party Services */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                10. Third-Party Services
              </h2>
              <p className='text-gray-700'>
                The Service may include links or integrations with third-party
                websites and services. We are not responsible for their privacy
                practices. Please review their privacy policies before sharing
                your information.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* AI and Personalization */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                11. AI and Personalization
              </h2>
              <p className='text-gray-700'>
                We may use your inputs, behavior, and content to enhance our
                AI-based features and improve recommendations, discovery
                experiences, and search personalization. AI models are trained
                using de-identified and aggregated data unless explicitly stated
                otherwise.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Changes to This Privacy Policy */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                12. Changes to This Privacy Policy
              </h2>
              <p className='text-gray-700'>
                We may update this Privacy Policy from time to time. If we make
                material changes, we will notify you via email, in-app notice,
                or website banner. Your continued use of the Service constitutes
                acceptance of the updated policy.
              </p>
            </section>

            <hr className='border-gray-300' />

            {/* Contact Us */}
            <section>
              <h2 className='text-2xl font-semibold text-gray-900 mb-4'>
                13. Contact Us
              </h2>
              <p className='text-gray-700 mb-2'>
                If you have questions or concerns about this Privacy Policy,
                please contact us:
              </p>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <p className='text-gray-700 font-semibold'>
                  Nexgo AI Labs Inc.
                </p>
                <p className='text-gray-700'>
                  16192 Coastal Highway, Lewes, Delaware 19958
                </p>
                <p className='text-gray-700'>
                  Email:{' '}
                  <a
                    href='mailto:legal@nexgo.ai'
                    className='text-blue-600 hover:text-blue-800'
                  >
                    legal@nexgo.ai
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
